import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Captcha } from '../components/Captcha';
import { toast } from 'sonner';

export function LoginPage() {
  const [formData, setFormData] = useState({ identifier: '', password: '', captcha_id: '', captcha_answer: '' });
  const [loading, setLoading] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData);
    setLoading(false);

    if (result.success) {
      toast.success(result.data?.reactivated
        ? 'Welcome back, your account is restored!'
        : 'Login successful!');
      navigate('/members');
    } else {
      toast.error(result.error);
      setFormData((prev) => ({ ...prev, captcha_answer: '' }));
      setCaptchaKey((k) => k + 1);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] p-8">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light mb-2 text-center" data-testid="login-heading">Welcome Back</h1>
          <p className="text-sm text-muted-foreground text-center mb-8">Sign in with your username or email</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="identifier">Username or Email</Label>
              <Input
                id="identifier"
                type="text"
                autoComplete="username"
                placeholder="johndoe or you@example.com"
                value={formData.identifier}
                onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                required
                data-testid="login-identifier-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="login-password-input"
              />
            </div>

            <Captcha
              answer={formData.captcha_answer}
              reloadKey={captchaKey}
              onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            />

            <Button
              type="submit"
              className="w-full rounded-full bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="text-center mt-4">
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-primary" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
